import { Icon, SvgIconNames } from '@toggle/design-system';
import React from 'react';

import {
  getSizingValues,
  InsightIconVariants,
  singleIconVariants,
  variantToBorderMap,
  variantToColorMap,
  verticalComboIconVariants,
} from './utils';

export interface InsightIconsProps {
  iconNames: SvgIconNames[];
  variant: InsightIconVariants;
  direction: 'positive' | 'negative';
  customSize?: { width: string; height: string };
}

export const InsightIcons = ({
  iconNames,
  variant,
  direction,
  customSize,
}: InsightIconsProps) => {
  const parsedIconNames = iconNames.filter(name => !!name);
  const hasIconNames = parsedIconNames.length > 0;

  const isSingleIcon = singleIconVariants.includes(variant);
  const isVerticalComboIcon = verticalComboIconVariants.includes(variant);
  const { stroke, fill } = variantToColorMap[variant];
  const fallbackIconName =
    direction === 'negative'
      ? 'MomentumStrongNegative'
      : 'MomentumStrongPositive';

  const iconSize = 24;
  const comboIconPadding = 3;

  const svgDimensions = isSingleIcon
    ? { width: 34, height: 40 }
    : isVerticalComboIcon
      ? { width: 34, height: 66 }
      : { width: 60, height: 40 }; // horizontal combo icon

  const borderIconName = variantToBorderMap[variant][direction];

  const { svgProps, xOffset, yOffset, comboStartX, comboStartY } =
    getSizingValues({
      width: svgDimensions.width,
      height: svgDimensions.height,
      iconSize,
      iconsCount: Math.max(parsedIconNames.length, 1),
      comboIconPadding,
    });

  const getTransform = (idx: number) =>
    isSingleIcon
      ? `translate(${xOffset}, ${yOffset})`
      : isVerticalComboIcon
        ? `translate(${xOffset}, ${
            comboStartY + idx * (iconSize + comboIconPadding)
          })`
        : `translate(${
            comboStartX + idx * (iconSize + comboIconPadding)
          }, ${yOffset})`;

  return (
    <svg {...svgProps} {...customSize} data-testid="insight-icons">
      <Icon
        iconName={borderIconName}
        fillColor={fill}
        strokeColor={stroke}
        {...svgProps}
      />
      {hasIconNames ? (
        parsedIconNames.map((iconName, idx) => (
          <g transform={getTransform(idx)} key={`${iconName}${idx}`}>
            <Icon iconName={iconName} fillColor={stroke} size={iconSize} />
          </g>
        ))
      ) : (
        <g transform={getTransform(0)}>
          <Icon
            iconName={fallbackIconName}
            fillColor={stroke}
            size={iconSize}
          />
        </g>
      )}
    </svg>
  );
};
